// Weekly Philosophy Challenges for Profound Philosophy
// One challenge surfaces per week on the dashboard (week-of-year rotation).

export const philosophyChallenges = [
  {
    id: 1,
    title: "The Question Collector",
    description: "Gather five genuinely open questions this week: questions you can't answer and can't stop thinking about. Write them down without attempting answers.",
    category: "Doing Philosophy",
    difficulty: "Beginner",
    timeCommitment: "A few minutes a day for 7 days",
    why: "Philosophy begins in wonder. Training yourself to notice and hold open questions, rather than rushing to close them, is the foundational habit every module builds on.",
    points: 10
  },
  {
    id: 2,
    title: "Socratic Week",
    description: "In five conversations, ask 'what makes you say that?' before offering your own view. Note what the question surfaces that a counter-argument wouldn't have.",
    category: "Doing Philosophy",
    difficulty: "Beginner",
    timeCommitment: "5 conversations",
    why: "The Socratic method reveals the reasoning under people's views, including your own. Questions open what assertions shut down.",
    points: 15
  },
  {
    id: 3,
    title: "Fallacy Spotting Safari",
    description: "Find seven logical fallacies in the wild this week: news, advertising, social media, meetings. Name each one and note what the argument should have established instead.",
    category: "Logic & Reasoning",
    difficulty: "Beginner",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "Fallacies are persuasive precisely because they go unnoticed. Naming them in real environments, not textbook examples, is what builds a working detector.",
    points: 15
  },
  {
    id: 4,
    title: "Steelman Sprint",
    description: "Three times this week, take a view you disagree with and write its strongest possible case in three sentences before responding to it.",
    category: "Logic & Reasoning",
    difficulty: "Intermediate",
    timeCommitment: "3 sessions (10 mins each)",
    why: "Refuting weak versions of opposing views teaches nothing. Steelmanning is the fastest route to understanding why intelligent people disagree with you.",
    points: 20
  },
  {
    id: 5,
    title: "Confidence Calibration Audit",
    description: "Before checking any fact this week, record your confidence as a percentage. Verify, then compare. Track at least ten predictions and review your hit rate.",
    category: "Knowledge & Truth",
    difficulty: "Intermediate",
    timeCommitment: "10 quick checks across the week",
    why: "Many people are more confident than their record supports. Calibration (matching certainty to evidence) is epistemology as a daily practice, and it's measurable.",
    points: 20
  },
  {
    id: 6,
    title: "The Disconfirmation Dive",
    description: "Choose one belief you'd hate to lose. Spend 30 minutes honestly seeking the strongest evidence and arguments against it. Write down the best you find.",
    category: "Knowledge & Truth",
    difficulty: "Advanced",
    timeCommitment: "One 30-minute session",
    why: "Confirmation bias comes naturally. Deliberately hunting disconfirming evidence is uncomfortable, which is exactly why it works.",
    points: 25
  },
  {
    id: 7,
    title: "Three-Lens Moral Review",
    description: "Take one real decision you face this week and analyse it three ways: by consequences, by duties, and by what a person of good character would do. Compare verdicts.",
    category: "Ethics",
    difficulty: "Intermediate",
    timeCommitment: "One 20-minute session",
    why: "Each ethical framework catches what the others miss. Running all three on a live decision, not a trolley problem, shows you which lens you default to.",
    points: 20
  },
  {
    id: 8,
    title: "Values-Behaviour Ledger",
    description: "Name your top three values, then track how yesterday and today were actually spent. Score the alignment honestly and pick one concrete adjustment for the rest of the week.",
    category: "Ethics",
    difficulty: "Beginner",
    timeCommitment: "15 mins, plus ongoing awareness",
    why: "The gap between stated values and lived behaviour is where ethical growth happens. You can't close a gap you haven't measured.",
    points: 15
  },
  {
    id: 9,
    title: "Identity Thread Hunt",
    description: "Interview yourself across time: what has completely changed about you in ten years, and what persisted? Write a paragraph naming the thread, then test whether it survives the Ship of Theseus.",
    category: "Metaphysics",
    difficulty: "Advanced",
    timeCommitment: "One 25-minute session",
    why: "Personal identity underwrites promises, responsibility and hope. Examining what actually connects you-then to you-now turns an abstract puzzle personal.",
    points: 25
  },
  {
    id: 10,
    title: "Attention to Experience",
    description: "Once a day, give one ordinary experience your full attention for two minutes (coffee, rain, traffic hum) and afterwards write one sentence on what it was like.",
    category: "Mind & Consciousness",
    difficulty: "Beginner",
    timeCommitment: "2 minutes daily",
    why: "Consciousness is the one thing you know from the inside, and the easiest thing to ignore. Attending to raw experience is fieldwork for the philosophy of mind.",
    points: 10
  },
  {
    id: 11,
    title: "Veil of Ignorance Week",
    description: "Re-examine three of your policy opinions as if you didn't know your income, health, age or postcode. Note which opinions move and which hold.",
    category: "Society & Justice",
    difficulty: "Intermediate",
    timeCommitment: "3 sessions (10 mins each)",
    why: "Rawls' veil separates principle from self-interest. Opinions that survive it are yours for better reasons; opinions that don't were never really about justice.",
    points: 20
  },
  {
    id: 12,
    title: "The Sisyphus Experiment",
    description: "Choose your most repetitive weekly task and do it each time with deliberate, full attention, as if the doing itself were the point. Journal what shifts by week's end.",
    category: "Meaning & Existence",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing through the week",
    why: "Camus' answer to absurdity isn't an argument; it's a stance. Practising meaning-in-the-doing on a real boulder of your own tests it better than any essay.",
    points: 20
  }
];

// Get the challenge of the week based on the current week of the year
export const getChallengeOfTheWeek = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now - start;
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  const weekOfYear = Math.floor(diff / oneWeek);
  const challengeIndex = weekOfYear % philosophyChallenges.length;
  return philosophyChallenges[challengeIndex];
};

// Get a random challenge
export const getRandomChallenge = () => {
  return philosophyChallenges[Math.floor(Math.random() * philosophyChallenges.length)];
};

// Get challenges by difficulty
export const getChallengesByDifficulty = (difficulty) => {
  return philosophyChallenges.filter(challenge => challenge.difficulty === difficulty);
};

// Get challenges by category
export const getChallengesByCategory = (category) => {
  return philosophyChallenges.filter(challenge => challenge.category === category);
};

// Get all categories
export const getChallengeCategories = () => {
  return [...new Set(philosophyChallenges.map(challenge => challenge.category))];
};

export default philosophyChallenges;
